import { initFeed } from '~/actions/feed';
import { initQuiz } from '~/actions/quiz';
import { initScenarios } from '~/actions/scenarios';
import { initWheel } from '~/actions/wheel';
import RS from '~/api';
import { initSessionController } from '~/api/sessionController';
import { consoleNotification } from '~/consoleNotification';
import { KEY_SETTINGS_STORAGE } from '~/constants';
import { getFromSessionStorage, saveToSessionStorage } from '~/helpers/storage';
import listenerService from '~/listenerEventService/ListenerService';
import { clickstreamLogger } from '~/logging';
import { initUtilities } from '~/utils';
import { rsaPublicKey } from '~/utils/RsaPublicKey';
import settings from '~/utils/Settings';
import SiteXMLService from '~/utils/SiteXMLService';
import { authenticateUser } from '~/utils/UserCredentials';
import { checkTimingsWidgets } from '~/utils/checkTimingsWidgets';

let enableTrackingEvents = false;
let enableTrackingScenarios = false;
let active = false;
let starting = false;
let initPromise: Promise<void> = null;
let stoppingTimer: number = null;

export const init = (
  isTrackingEvents = true,
  isTrackingScenarios = true
) => {
  enableTrackingEvents =
    typeof isTrackingEvents === 'boolean'
      ? isTrackingEvents
      : false;
  enableTrackingScenarios =
    typeof isTrackingScenarios === 'boolean'
      ? isTrackingScenarios
      : false;

  if (active === true) {
    clickstreamLogger.warn(
      'ClickStream is already running. Just updating user data.'
    );
    authenticateUser().catch((error) => {
      clickstreamLogger.error('Error on updating user data: ', error);
    });

    rsaPublicKey().catch((error) => {
      clickstreamLogger.error(['Error on updating key: ', error]);
    });

    return;
  }

  consoleNotification(enableTrackingEvents, enableTrackingScenarios);

  initUtilities();

  if (enableTrackingEvents || enableTrackingScenarios) {
    initPromise = rsaPublicKey()
      .then(authenticateUser)
      .then(() => {
        const settingsFromStorage = getFromSessionStorage(
          KEY_SETTINGS_STORAGE,
          'settings'
        );
        if (settingsFromStorage) {
          return Promise.resolve();
        }

        return RS.settings.get().then((response) => {
          // TODO: это должно работать по-другому
          // прикрутить понятную обработку ошибок ARCH-295
          if (response.errorData?.errorType === 'ORIGIN_NOT_FOUND') {
            throw new Error('ORIGIN_NOT_FOUND');
          }
          saveToSessionStorage(
            KEY_SETTINGS_STORAGE,
            'settings',
            response.payload
          );
        });
      })
      .then(() => {
        // Инициализация контроллера сессии
        return initSessionController();
      })
      .then(() => {

        return checkTimingsWidgets();
      })
      .then(() => enableTrackingScenarios ? initScenarios() : Promise.resolve());
  }

};

export const start = () => {
  window.clearTimeout(stoppingTimer);

  if (active || starting) {
    return;
  }

  if (!initPromise) {
    clickstreamLogger.warn(
      'ClickStream module has not been initialized. Skipping.'
    );

    return;
  }

  starting = true;

  if (stoppingTimer) {
    window.clearTimeout(stoppingTimer);
    stoppingTimer = null;

    return;
  }

  initPromise
    .then(() => {
      // На этом этапе мы получили настройки,
      // поэтому обновляем уровень логирования.
      clickstreamLogger.updateLoggingLevel(settings.getOrDefault('LOGGING_LEVEL', 2));
      if (enableTrackingEvents || enableTrackingScenarios) {
        const { ANALYTICS_ENABLED } = settings.settingsMapping as any;
        if (ANALYTICS_ENABLED === 0) {
          enableTrackingEvents = false;
        }
        listenerService.start(enableTrackingEvents, enableTrackingScenarios);
        active = true;
      }
      // Запуск лент
      const { FEED_ENABLED } = settings.settingsMapping as any;
      if (FEED_ENABLED === 1) {
        initFeed();
      }
      // Запуск колеса фортуны
      initWheel();
      // Запуск квизов
      initQuiz();
      // Запуск сайт-мапы
      const siteXMLService = new SiteXMLService();
      siteXMLService.start();
      starting = false;
      clickstreamLogger.debug('ClickStreamAnalytics started successfully');
    })
    .catch((error) => {
      active = false;
      starting = false;
      stop();
      clickstreamLogger.error('Error happened on clickstream initialization: ', error);
    });
};

const stopListenerService = () => {
  if (active) {
    listenerService.stop();
    active = false;
  }
  stoppingTimer = null;
};

export const stop = () => {
  if (!active) {
    return;
  }

  if (stoppingTimer) {
    window.clearTimeout(stoppingTimer);
  }

  stoppingTimer = window.setTimeout(
    stopListenerService,
    settings.getOrDefault('STOP_APPLICATION_DELAY', 500)
  );
};

export const isActive = () => {
  return active;
};

